import { ZButton } from "components/ZButton";
import { ZIcon } from "components/ZIcon";
import { ZTooltip } from "components/ZTooltip";
import { ZIconType } from "helpers/utility/iconMap";
import { ApnPdeStockResult, ApnPdeStockType } from "models/dbModels/apnPdes/ApnPdeStockResult";
import { ManagedStockView } from "models/dbModels/managedStock/ManagedStock";
import { CombinedPdeOrApn, ManagedApnPdeType } from "models/dbModels/pdes/CombinedPdes";
import { Col, Modal, Row } from "react-bootstrap";
import { FieldErrors, FieldValues } from "react-hook-form";
import { Link } from "react-router-dom";


export type ErrorInformation = {
	heading: string;
	messages: string[];
	link?: string;
	buttonInfo?: {
		buttonText: string;
		loadingButtonText?: string;
		buttonAction: () => void;
		loading?: boolean;
	}
}

export type ErrorHandlerProps = {
    errorInfo?: ErrorInformation;
    showError?: boolean;
    onBack?: () => void;
}

export enum ErrorCase {
    New_System,
    New_CustomManaged,
    New_SystemManaged,

    UpdateCustom_System,
    UpdateCustom_SystemManaged,
    UpdateCustom_CustomManaged,

    UpdateSystem_System,
    UpdateSystem_SystemManaged,
    UpdateSystem_CustomManaged,

    Unknown
}

export function getModelDataForImport<T extends FieldValues>(formErrors: FieldErrors<T>, model: T): Partial<T> {
    // Get props from formErrors so we can match them up to the model props and see which ones exist on formErrors (this gives us the invalid fields and allows us to exclude those from the import)
    const formErrorKeys = Object.keys(formErrors) as (keyof FieldErrors<ManagedStockView>)[];

    // Get the props of model that don't exist or are undefined on formErrors (AKA the valid props)
    const validProps = Object.keys(model).filter(k => {
        const errorKey = formErrorKeys.find(errorKey => errorKey === k);
        return errorKey ? formErrors[errorKey] == null : true;
    }) as (keyof T)[];

    // I'm sure there's a better way of doing this but it works.
    // We define a partial type of T, since our valid props will be a subset of T
    let subset: Partial<T> = {};

    // Then we build the subset up by iterating over the valid props and grabbing putting the value from model into subset
    validProps.forEach(prop => {
        subset = {
            ...subset,
            [prop]: model[prop]
        }
    });

    return subset;
}

export function getErrorCase(dupe: ApnPdeStockResult, stockModel: ManagedStockView): ErrorCase {
    // Set up error variables
    // There are several cases we have to consider
    // 1. We are creating a new managed stock and:
    if (!stockModel.stockId) {
        switch(dupe.parentType) {
            // a) Duplicate PDE belongs to a system stock
            //		=> Import this system stock with valid managed stock data
            case ApnPdeStockType.System:
                return ErrorCase.New_System;
            // b) Duplicate PDE belongs to a managed stock with or without a linked system stock
            //		=> Block further progress but offer to navigate to the existing managed stock
            case ApnPdeStockType.Managed:
                if (dupe.linkedToSystem) {
                    return ErrorCase.New_SystemManaged;
                } else {
                    return ErrorCase.New_CustomManaged;
                }
            default:
                return ErrorCase.Unknown
        }
    } else {
        // Updating a managed stock...
        if (stockModel.productNo) {
            // This is a managed system stock
            if (dupe.parentType === ApnPdeStockType.System) {
                return ErrorCase.UpdateSystem_System;
            } else if (dupe.linkedToSystem) {
                return ErrorCase.UpdateSystem_SystemManaged;
            } else {
                return ErrorCase.UpdateSystem_CustomManaged;
            }
        } else {
            // This is a custom managed stock
            if(dupe.parentType === ApnPdeStockType.System) {
                return ErrorCase.UpdateCustom_System;
            } else if (dupe.linkedToSystem) {
                return ErrorCase.UpdateCustom_SystemManaged;
            } else {
                return ErrorCase.UpdateCustom_CustomManaged;
            }
        }
    }
}

export function ErrorHandler({ errorInfo, showError, onBack }: ErrorHandlerProps) {
    return (
        <Modal className="modal-fit" show={showError} centered backdrop="static">
			<Modal.Header>
				<Modal.Title><ZIcon icon={ZIconType.Alert} variant='warning' iconClassName='me-1'/>{errorInfo && errorInfo.heading}</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				{errorInfo ? errorInfo.messages.map((m, ix) => {
					return (
						<p key={ix} className={`${ix === errorInfo.messages.length - 1 ? "mb-0" : ""}`}>{m}</p>
					)
				}) : <p className="mb-0">An unknown error has occurred.</p>}
			</Modal.Body>
			<Modal.Footer>
				<Row>
					<Col xs={12} className="d-flex flex-row-reverse gap-2 pe-0">
						{errorInfo && errorInfo.link && 
							<ZTooltip
								text="Open stock in new tab"
							>
								<Link className="d-flex align-items-center" to={errorInfo.link} target={'_blank'}>
									<ZIcon icon={ZIconType.NewTab} variant="info"/>
								</Link>
							</ZTooltip>
						}
						{errorInfo && errorInfo.buttonInfo && <ZButton className="cta-button" loading={errorInfo.buttonInfo.loading} variant="primary" buttonText={errorInfo.buttonInfo.loading ? errorInfo.buttonInfo.loadingButtonText ?? errorInfo.buttonInfo.buttonText : errorInfo.buttonInfo.buttonText} onClick={errorInfo.buttonInfo.buttonAction} />}
						<ZButton className="cta-button" variant="light" buttonText="Go back" onClick={onBack} />
					</Col>
				</Row>
			</Modal.Footer>
		</Modal>
    )
}

